// Shared low-level ArcGIS REST query transport (BBB-457).
//
// One home for "POST a query to an ArcGIS layer and interpret the response",
// used by both the zone-land extractor (polygon intersect) and the map vector
// outline adapter (viewport envelope). ArcGIS servers report errors inside a
// 200 response body and signal capped results with exceededTransferLimit, so
// both of those quirks are handled here rather than at each call site.
//
// The request is bounded by fetchTextWithTimeout, which is also where the body
// is now read. Both callers query servers we do not own, and an unbounded
// request is how a panel ends up loading for the rest of the session (BBB-527).

import { fetchTextWithTimeout } from "./fetchWithTimeout";

/**
 * UTF-8 byte length of a string.
 *
 * BBB-525: this used to be `body.length`, which counts UTF-16 code units and is
 * only correct for pure ASCII. Every accented place name in a `NAME` field, and
 * every degree sign in an ArcGIS error message, made the reported size too
 * small; an emoji or any astral character made it half the truth. Metrics only,
 * and the harness is off by default, but a size measurement that is wrong for
 * non-ASCII is worse than no measurement because it looks authoritative.
 *
 * Falls back to counting the encoding by hand where `TextEncoder` is absent,
 * rather than silently returning the wrong number again.
 *
 * @param {string} text
 * @returns {number} bytes
 */
export function utf8ByteLength(text) {
  if (typeof text !== "string") return 0;
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(text).length;
  }
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.codePointAt(i);
    if (code <= 0x7f) bytes += 1;
    else if (code <= 0x7ff) bytes += 2;
    else if (code <= 0xffff) bytes += 3;
    else {
      // Astral plane: one code point, two UTF-16 units, four UTF-8 bytes.
      bytes += 4;
      i += 1;
    }
  }
  return bytes;
}

/**
 * POST a form-encoded query to an ArcGIS REST layer and return its features.
 *
 * @param {string} url layer URL, without the trailing `/query`
 * @param {URLSearchParams} params query parameters
 * @param {string} id caller-facing identifier, used in error messages
 * @returns {Promise<{features: Array, truncated: boolean, ms: number, bytes: number}>}
 *   ms and bytes are always measured — they cost nothing and let a caller report
 *   them without this transport needing to know anything about metrics. bytes is
 *   the uncompressed body length in UTF-8 bytes, so it overstates what crossed
 *   the wire on a gzipped response; it is the right number for comparing layers,
 *   not for quoting a farmer's data usage.
 */
export async function postArcgisQuery(url, params, id) {
  const startedAt =
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();

  // An ArcGIS Server outage answers 200 with an HTML page from the web adaptor,
  // not JSON, so parsing first would throw "Unexpected token '<'" and report a
  // malformed response at exactly the moment it needs to say the server is down.
  // Measured 18/08/2026 against druid.hutton.ac.uk, host-wide down since 14/08:
  // every service AND /arcgis/rest/info returned 200, and /query returned the
  // adaptor's "Could not access any server machines" error page. Four days of a
  // dead upstream reading as our own JSON bug is how it stayed misdiagnosed.
  //
  // Reading text() and parsing it by hand also makes the body size knowable for
  // the metrics harness (BBB-523). Same work either way: json() reads the text
  // and parses it too. The two reasons want the same code, so the outage
  // detection below and the ms/bytes measurement here are not alternatives.
  const { ok, status, body } = await fetchTextWithTimeout(
    `${url}/query`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    },
    `ArcGIS query for ${id}`
  );

  const ms =
    (typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now()) - startedAt;

  if (!ok) {
    throw new Error(`ArcGIS query failed for ${id}: HTTP ${status}`);
  }

  let data;
  try {
    data = JSON.parse(body);
  } catch {
    const looksLikeHtml = /^\s*<(?:!doctype|html)/i.test(body);
    throw new Error(
      looksLikeHtml
        ? `ArcGIS query failed for ${id}: the server returned an HTML error page, not data. The service is probably down.`
        : `ArcGIS query failed for ${id}: the response was not JSON.`
    );
  }

  // JSON.parse succeeds on the literals `null`, `"a string"` and `42`, so a
  // parse alone does not prove we have an object. Without this guard a body of
  // `null` reaches `data.error` and throws a TypeError, which surfaces to the
  // caller as our own bug rather than as "the server sent us nothing usable".
  //
  // Arrays are rejected here too, and they are the quieter failure: `typeof []`
  // is "object", so a JSON array body would pass this guard, carry no `error`
  // and no `status`, and fall through to `data.features || []` — reporting zero
  // features for a zone that may well have some, with nothing raised.
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    throw new Error(
      `ArcGIS query failed for ${id}: the response was JSON but not an object.`
    );
  }

  // ArcGIS reports errors inside a 200 response body, in TWO different shapes.
  //
  // `error` is the documented per-request shape a live server returns for a bad
  // query: {"error":{"code":500,"message":"..."}}. `status: "error"` with a
  // `messages` array comes from the web adaptor when the ArcGIS Server site
  // behind it is unreachable, and has no `error` key at all — so guarding only
  // the documented shape let an outage fall straight through to
  // `data.features || []` and return zero features with no error raised. Silence,
  // and indistinguishable from a zone that genuinely has no features.
  if (data.error) {
    throw new Error(
      `ArcGIS query failed for ${id}: ${data.error.message || JSON.stringify(data.error)}`
    );
  }

  if (data.status === "error") {
    const detail = Array.isArray(data.messages)
      ? data.messages.join("; ")
      : JSON.stringify(data.messages ?? data);
    throw new Error(`ArcGIS query failed for ${id}: ${detail}`);
  }

  // Capped result: top-level on esriJSON, under properties on the geojson shape.
  const truncated = Boolean(
    data.exceededTransferLimit || data.properties?.exceededTransferLimit
  );

  return { features: data.features || [], truncated, ms, bytes: utf8ByteLength(body) };
}
